import React, { useState, useEffect } from "react";
import {
  Plus,
  MessageSquare,
  Calendar,
  User,
  Edit2,
  Trash2,
  Mail,
  Phone,
  MessageCircle,
  Send,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  noteTypes,
  messageStatuses,
  formatTimeAgo,
  usePatientNotesData,
  CommunicationType,
  NoteType,
} from "@/lib/dashboard/patientNotesData";
import { NumberIsUKFormat } from "@/utils/validMobile";
import useTitanApp from "@/hooks/useTitanApp";

const SMS_SEGMENT_LENGTH = 160;

interface SmsSegmentInfo {
  length: number;
  segments: number;
  singleLimit: number;
  remaining: number;
}

function getSmsSegmentInfo(text: string): SmsSegmentInfo {
  const length = Array.from(text).length;
  const segments =
    length === 0 ? 0 : Math.ceil(length / SMS_SEGMENT_LENGTH);
  const used = length % SMS_SEGMENT_LENGTH || SMS_SEGMENT_LENGTH;
  const remaining = length === 0 ? SMS_SEGMENT_LENGTH : SMS_SEGMENT_LENGTH - used;

  return { length, segments, singleLimit: SMS_SEGMENT_LENGTH, remaining };
}

interface PatientNotesProps {
  patientId: string;
  patientEmail?: string;
  patientPhone?: string;
  isDarkMode: boolean;
  onSaveNote?: (noteData: any) => void;
  onEditNote?: (id: string, noteData: any) => void;
  onDeleteNote?: (id: string) => void;
  onSendEmail?: (content: string) => void;
  onSendSMS?: (content: string) => void;
  onSendInAppMessage?: (content: string) => void;
}

// Notes data - in real app this would come from props/API

export function PatientNotes({
  patientId,
  patientEmail,
  patientPhone,
  isDarkMode,
  onEditNote,
  onDeleteNote,
}: PatientNotesProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [newNoteType, setNewNoteType] = useState<
    "general" | "medical" | "administrative"
  >("general");
  const [showCommunication, setShowCommunication] = useState(false);
  const [communicationMessage, setCommunicationMessage] = useState("");
  const [communicationType, setCommunicationType] = useState<
    "email" | "sms" | "in-app"
  >("email");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [showInvalidPhoneDialog, setShowInvalidPhoneDialog] = useState(false);

  const smsInfo = getSmsSegmentInfo(communicationMessage);

  // Use the hook to manage patient communications and notes
  const { notes, isLoading, isSending, error, addCommunication, deleteNotes } =
    usePatientNotesData();
  const handleAddNote = async () => {
    if (newNoteContent.trim()) {
      // Map string type to enum
      const typeMap: Record<string, NoteType> = {
        general: NoteType.General,
        medical: NoteType.Medical,
        administrative: NoteType.Administrative,
      };
      const noteTypeEnum = typeMap[newNoteType] || NoteType.General;

      await addCommunication(
        newNoteContent.trim(),
        CommunicationType.InApp,
        noteTypeEnum,
      );
      setNewNoteContent("");
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    setNoteToDelete(noteId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (noteToDelete) {
      await deleteNotes(noteToDelete);
      setShowDeleteConfirm(false);
      setNoteToDelete(null);
    }
  };

  const handleSendCommunication = async (
    commType: string,
    patientPhone: string,
  ) => {
    if (commType === "sms" && !NumberIsUKFormat(patientPhone)) {
      setShowInvalidPhoneDialog(true);
      return;
    }

    if (communicationMessage.trim()) {
      // Map communication type to enum
      const commTypeMap: Record<string, CommunicationType> = {
        email: CommunicationType.Email,
        sms: CommunicationType.Sms,
        "in-app": CommunicationType.InApp,
      };
      const commTypeEnum = commTypeMap[communicationType];
      // Call API to add communication
      const newNote = await addCommunication(
        communicationMessage.trim(),
        commTypeEnum, // 0=Email, 1=SMS, 2=InApp
        NoteType.Communication,
      );

      setCommunicationMessage("");
      setShowCommunication(false);
    }
  };

  const getNoteTypeColor = (type: string) => {
    const noteType = noteTypes.find((nt) => nt.value === type);
    return noteType?.color || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getCommunicationIcon = (communicationType?: string) => {
    switch (communicationType) {
      case "email":
        return <Mail className="w-3 h-3" />;
      case "sms":
        return <Phone className="w-3 h-3" />;
      case "in-app":
        return <MessageCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status?: string) => {
    const messageStatus = messageStatuses.find((ms) => ms.value === status);
    return messageStatus?.color || "text-gray-600";
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3
          className={`text-sm font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Notes & Communication
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCommunication(true)}
            className={`p-0 h-auto font-normal transition-colors duration-300 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
            title="Send message to patient"
          >
            <Send className="w-4 h-4 mr-1" />
            Message
          </button>
          <button
            onClick={() => setIsAddingNote(true)}
            className={`p-0 h-auto font-normal transition-colors duration-300 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Note
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            isDarkMode
              ? "bg-red-900 border border-red-700 text-red-100"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Communication Form */}
      {showCommunication && (
        <div
          className={`${
            isDarkMode
              ? "bg-gray-700 border-gray-600"
              : "bg-white border-gray-200"
          } rounded-lg border p-3 mb-4 transition-colors duration-300`}
        >
          <div className="flex items-center gap-2 mb-3">
            <Send className="w-4 h-4" />
            <span className="text-sm font-medium">Send Message to Patient</span>
          </div>

          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setCommunicationType("email")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${
                communicationType === "email"
                  ? isDarkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-500 text-white"
                  : isDarkMode
                    ? "bg-gray-600 text-gray-300 hover:bg-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              disabled={!patientEmail}
              title={
                !patientEmail ? "No email address available" : "Send via email"
              }
            >
              <Mail className="w-3 h-3" />
              Email
              {!patientEmail && (
                <span className="text-xs opacity-75">(N/A)</span>
              )}
            </button>

            <button
              onClick={() => setCommunicationType("sms")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${
                communicationType === "sms"
                  ? isDarkMode
                    ? "bg-green-600 text-white"
                    : "bg-green-500 text-white"
                  : isDarkMode
                    ? "bg-gray-600 text-gray-300 hover:bg-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              disabled={!patientPhone}
              title={
                !patientPhone ? "No phone number available" : "Send via SMS"
              }
            >
              <Phone className="w-3 h-3" />
              SMS
              {!patientPhone && (
                <span className="text-xs opacity-75">(N/A)</span>
              )}
            </button>
          </div>

          <textarea
            value={communicationMessage}
            onChange={(e) => setCommunicationMessage(e.target.value)}
            placeholder={`Compose ${communicationType} message...`}
            className={`w-full p-2 text-sm rounded border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
            }`}
            disabled={isSending}
            rows={3}
          />

          {communicationType === "sms" && (
            <div className="mt-1">
              <div
                className={`text-[11px] ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                UK networks split SMS into multiple messages after{" "}
                {smsInfo.singleLimit} characters.
              </div>
              <div
                className={`text-xs mt-0.5 font-medium ${
                  smsInfo.segments > 1
                    ? "text-amber-600"
                    : isDarkMode
                      ? "text-gray-300"
                      : "text-gray-600"
                }`}
              >
                {smsInfo.length} character{smsInfo.length === 1 ? "" : "s"}
                {smsInfo.segments > 1 && ` · ${smsInfo.remaining} left in this part`}
                {" · "}
                {smsInfo.segments <= 1 ? 1 : smsInfo.segments} SMS message
                {smsInfo.segments > 1 ? "s" : ""}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-gray-500">
              {communicationType === "email" &&
                patientEmail &&
                `To: ${patientEmail}`}
              {communicationType === "sms" &&
                patientPhone &&
                `To: ${patientPhone}`}
              {communicationType === "in-app" &&
                "Will be sent to patient's app inbox"}
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCommunication(false);
                  setCommunicationMessage("");
                }}
                className="text-xs"
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  handleSendCommunication(communicationType, patientPhone)
                }
                size="sm"
                className="text-xs"
                disabled={
                  !communicationMessage.trim() ||
                  (communicationType === "email" && !patientEmail) ||
                  (communicationType === "sms" && !patientPhone) ||
                  isSending
                }
              >
                {isSending ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="w-3 h-3 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <>
                    Send{" "}
                    {communicationType === "sms"
                      ? "SMS"
                      : communicationType === "email"
                        ? "Email"
                        : "Message"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Form */}
      {isAddingNote && (
        <div
          className={`${
            isDarkMode
              ? "bg-gray-700 border-gray-600"
              : "bg-white border-gray-200"
          } rounded-lg border p-3 mb-4 transition-colors duration-300`}
        >
          <textarea
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            placeholder="Add a note..."
            className={`w-full p-2 text-sm rounded border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
            }`}
            disabled={isSending}
            rows={3}
            autoFocus
          />

          <div className="flex items-center justify-between mt-3">
            <select
              value={newNoteType}
              onChange={(e) => setNewNoteType(e.target.value as any)}
              className={`text-xs px-2 py-1 rounded border ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="general">General</option>
              <option value="medical">Medical</option>
              <option value="administrative">Administrative</option>
            </select>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNoteContent("");
                }}
                className="text-xs"
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddNote}
                size="sm"
                className="text-xs"
                disabled={!newNoteContent.trim() || isSending}
              >
                {isSending ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="w-3 h-3 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Save Note"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notes List (scrollable independent area) */}
      <div className="space-y-3 max-h-[85vh] overflow-y-auto pr-2 notes-scroll">
        {isLoading ? (
          // Loading Skeleton
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-white border-gray-200"
                } rounded-lg border p-3 `}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-6 w-16 rounded ${
                        isDarkMode ? "bg-gray-600" : "bg-gray-300"
                      }`}
                    ></div>
                    <div
                      className={`h-3 w-24 rounded ${
                        isDarkMode ? "bg-gray-600" : "bg-gray-300"
                      }`}
                    ></div>
                  </div>
                </div>
                <div
                  className={`h-12 w-full rounded mb-2 ${
                    isDarkMode ? "bg-gray-600" : "bg-gray-300"
                  }`}
                ></div>
              </div>
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div
            className={`text-center py-8 border rounded-lg transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notes recorded</p>
            <p className="text-sm mt-1 opacity-75">
              Patient notes will appear here once added
            </p>
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className={`group ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-200"
              } rounded-lg border p-3 transition-colors duration-300 hover:shadow-sm`}
            >
              {/* Note Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`text-xs flex items-center gap-1 ${getNoteTypeColor(
                      note.type,
                    )}`}
                  >
                    {note.type === "communication" &&
                      getCommunicationIcon(note.communicationType)}
                    {note.type}
                  </Badge>
                  {note.type === "communication" && note.messageStatus && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${getStatusColor(
                        note.messageStatus,
                      )}`}
                    >
                      {note.messageStatus}
                    </Badge>
                  )}
                  <span
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {formatTimeAgo(note.timestamp)}
                  </span>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Edit button commented out per request
                  <button
                    className={`p-1 rounded ${
                      isDarkMode ? "hover:bg-gray-600" : "hover:bg-gray-100"
                    }`}
                    title="Edit note"
                  >
                    <Edit2 className="w-3 h-3 text-gray-500" />
                  </button>
                  */}

                  {/* Show delete only for in-app communications */}
                  {note.communicationType === "in-app" && (
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className={`p-1 rounded ${
                        isDarkMode ? "hover:bg-gray-600" : "hover:bg-gray-100"
                      }`}
                      title="Delete note"
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  )}
                </div>
              </div>

              {/* Note Content */}
              <p
                className={`text-sm leading-relaxed mb-2 ${
                  isDarkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                {note.content}
              </p>

              {/* Note Footer */}
              <div className="flex items-center gap-2 text-xs">
                <User className="w-3 h-3" />
                <span
                  className={`${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {note.author}
                </span>
                <span
                  className={`${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  •
                </span>
                <Calendar className="w-3 h-3" />
                <span
                  className={`${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {note.timestamp.toLocaleDateString()}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent
          className={`${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className={`${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Delete Note
            </AlertDialogTitle>
            <AlertDialogDescription
              className={`${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              Are you sure you want to delete this note? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={`${
                isDarkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-900"
              }`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showInvalidPhoneDialog}
        onOpenChange={setShowInvalidPhoneDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invalid UK Mobile Number</DialogTitle>
            <DialogDescription>
              SMS can only be sent to UK numbers only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowInvalidPhoneDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
